import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Sparkles,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useContact } from '@/hooks/crm/useContacts';
import { useContactThreadLink } from '@/hooks/crm/useContactThreadLink';
import {
  classifyContactDetailError,
  normalizeCrmContactId,
} from '@/lib/crm/contact-detail-state';
import type { Contact } from '@/lib/crm/types';
import { CRM_AI_ENABLED } from '@/lib/crm-ai-feature';
import { formatUiDate } from '@/lib/ui-format';
import { ActivityTimeline } from '@/pages/crm/ActivityTimeline';
import { ContactEditCard } from '@/pages/crm/ContactEditCard';
import { LogReplyDialog } from '@/components/crm/LogReplyDialog';

function safeExternalUrl(value: string | null): string | undefined {
  if (!value) return undefined;

  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
      ? parsed.toString()
      : undefined;
  } catch {
    return undefined;
  }
}

export default function ContactDetailPage(): JSX.Element {
  const { contactId: routeContactId } = useParams<{ contactId: string }>();
  const location = useLocation();
  const contactId = normalizeCrmContactId(routeContactId);
  const contactQuery = useContact(contactId);
  const contact = contactQuery.data;
  const threadLink = useContactThreadLink(contact);
  const [loggingReply, setLoggingReply] = useState(false);

  useDocumentTitle(`${contact?.name || 'Contact'} | Tacit CRM`);

  if (!contactId) {
    return (
      <Layout>
        <ContactUnavailable />
      </Layout>
    );
  }

  if (contactQuery.isLoading) {
    return (
      <Layout>
        <div
          className="space-y-6 p-4 sm:p-6"
          role="status"
          aria-label="Loading contact"
        >
          <h1 className="text-2xl font-bold sm:text-3xl">Contact details</h1>
          <Skeleton className="h-8 w-full max-w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (contactQuery.isError) {
    const failure = classifyContactDetailError(contactQuery.error);

    if (failure.kind === 'unavailable') {
      return (
        <Layout>
          <ContactUnavailable />
        </Layout>
      );
    }

    if (failure.kind === 'auth') {
      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      return (
        <Layout>
          <ContactStateCard
            title="Sign in to continue"
            description="Your session is missing or has expired."
          >
            <Button asChild className="min-h-11">
              <Link to={`/login?returnTo=${encodeURIComponent(returnTo)}`}>
                Sign in
              </Link>
            </Button>
          </ContactStateCard>
        </Layout>
      );
    }

    return (
      <Layout>
        <ContactStateCard
          title="We couldn't load this contact"
          description={`${failure.safeError.description} ${failure.safeError.recovery}`}
          role="alert"
        >
          <Button
            className="min-h-11"
            onClick={() => void contactQuery.refetch()}
          >
            Retry
          </Button>
        </ContactStateCard>
      </Layout>
    );
  }

  if (!contact) {
    return (
      <Layout>
        <ContactUnavailable />
      </Layout>
    );
  }

  const linkedinUrl = safeExternalUrl(contact.linkedin_url);
  const contactIdentity = [contact.role, contact.company]
    .filter(Boolean)
    .join(' at ');

  return (
    <Layout>
      <div className="mx-auto w-full max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
        <header className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
          >
            <Link to="/crm" aria-label="Back to CRM contacts">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
          <div className="min-w-0 flex-1">
            <h1
              className="break-words text-2xl font-bold sm:text-3xl"
              data-content-origin="external"
            >
              {contact.name}
            </h1>
            {contactIdentity ? (
              <p
                className="mt-1 break-words text-muted-foreground"
                data-content-origin="external"
              >
                {contactIdentity}
              </p>
            ) : (
              <p className="mt-1 break-words text-muted-foreground">
                Contact details
              </p>
            )}
          </div>

          {threadLink.data?.heyreachLeadId && (
            <Button asChild variant="outline" className="min-h-11">
              <Link to={`/leads/${encodeURIComponent(threadLink.data.heyreachLeadId)}`}>
                Open conversation dossier
              </Link>
            </Button>
          )}
        </header>

        <div className="grid grid-cols-1 gap-6">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {contact.email && (
                  <InfoRow
                    icon={Mail}
                    label="Email"
                    value={(
                      <a
                        href={`mailto:${contact.email}`}
                        className="break-all text-primary hover:underline"
                      >
                        <span data-content-origin="external">{contact.email}</span>
                      </a>
                    )}
                  />
                )}

                {contact.phone && (
                  <InfoRow
                    icon={Phone}
                    label="Phone"
                    value={(
                      <a
                        href={`tel:${contact.phone}`}
                        className="text-primary hover:underline"
                      >
                        <span data-content-origin="external">{contact.phone}</span>
                      </a>
                    )}
                  />
                )}

                {linkedinUrl && (
                  <InfoRow
                    icon={Linkedin}
                    label="LinkedIn"
                    value={(
                      <a
                        href={linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        View profile
                      </a>
                    )}
                  />
                )}

                <InfoRow
                  icon={Building2}
                  label="Company"
                  value={(
                    <span data-content-origin="external">{contact.company}</span>
                  )}
                />

                {contact.role && (
                  <InfoRow
                    icon={Briefcase}
                    label="Role"
                    value={(
                      <span data-content-origin="external">{contact.role}</span>
                    )}
                  />
                )}

                {contact.location && (
                  <InfoRow
                    icon={MapPin}
                    label="Location"
                    value={(
                      <span data-content-origin="external">{contact.location}</span>
                    )}
                  />
                )}

                {contact.created_at && (
                  <InfoRow
                    icon={Calendar}
                    label="Added"
                    value={formatUiDate(contact.created_at)}
                  />
                )}
              </CardContent>
            </Card>

            <ContactEditCard contact={contact as Contact} />

            <Card>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div>
                  <p className="text-sm font-medium">Replied outside Tacit?</p>
                  <p className="text-sm text-muted-foreground">
                    Record it so this contact stops asking for a response it already got.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11 md:min-h-11"
                  onClick={() => setLoggingReply(true)}
                >
                  I replied
                </Button>
              </CardContent>
            </Card>

            <ActivityTimeline contactId={contact.id} />

            {CRM_AI_ENABLED && (
              <ContactAiInsights hasSavedContext={contact.ai_metadata !== null} />
            )}
          </div>

        </div>
      </div>

      {loggingReply && (
        <LogReplyDialog
          contact={contact as Contact}
          onClose={() => setLoggingReply(false)}
        />
      )}
    </Layout>
  );
}

function ContactUnavailable(): JSX.Element {
  return (
    <ContactStateCard
      title="Contact unavailable"
      description="This contact does not exist or you do not have access to it."
    >
      <Button asChild variant="outline" className="min-h-11">
        <Link to="/crm">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to CRM
        </Link>
      </Button>
    </ContactStateCard>
  );
}

export function ContactAiInsights({
  hasSavedContext,
}: {
  hasSavedContext: boolean;
}): JSX.Element {
  return (
    <Card role="region" aria-label="CRM AI insights">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          CRM AI insights
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          {hasSavedContext
            ? 'Saved AI context is available for this contact.'
            : 'No saved AI context is available for this contact.'}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Opening this page never generates or updates AI content.
        </p>
      </CardContent>
    </Card>
  );
}

interface ContactStateCardProps {
  title: string;
  description: string;
  role?: 'alert' | 'status';
  children: React.ReactNode;
}

function ContactStateCard({
  title,
  description,
  role = 'status',
  children,
}: ContactStateCardProps): JSX.Element {
  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <Card>
        <CardContent className="p-8 text-center sm:p-12" role={role}>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
            {description}
          </p>
          <div className="mt-6 flex justify-center">{children}</div>
        </CardContent>
      </Card>
    </div>
  );
}

interface InfoRowProps {
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  label: string;
  value: React.ReactNode;
}

function InfoRow({ icon: Icon, label, value }: InfoRowProps): JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className="break-words text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}
